import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import VideocamIcon from "@mui/icons-material/Videocam";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import { formatLogsForDisplay } from "../../../utils/testCaseFailureUtils";

/**
 * Shared attempt shape for Playwright (and later Cucumber / generic runners).
 * Backend should populate: label, status, duration, errorMessage?, logs?, screenshot?, video?
 */
export const getRetryAttemptKey = (attempt, index) =>
  attempt.attemptNumber ?? attempt.retryIndex ?? index;

const formatTime = (timeInSeconds) => {
  if (timeInSeconds === undefined || timeInSeconds === null || isNaN(timeInSeconds)) {
    return "00:00:00";
  }
  const totalSeconds = Math.floor(parseFloat(timeInSeconds));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((v) => (v < 10 ? "0" + v : v))
    .join(":");
};

const statusColor = (status, theme) => {
  if (status === "Pass") return theme.palette.success.main;
  if (status === "Fail") return theme.palette.error.main;
  return theme.palette.warning?.main || theme.palette.orange?.main;
};

const AttemptDetailPanel = ({ attempt, theme, isLast, columnCount }) => {
  const logsText = formatLogsForDisplay(attempt.logs);

  return (
    <TableRow
      sx={{
        bgcolor:
          theme.palette.mode === "dark"
            ? "rgba(99, 102, 241, 0.04)"
            : "rgba(248, 250, 252, 0.9)",
      }}
    >
      <TableCell
        colSpan={columnCount}
        sx={{
          py: 1.5,
          pl: 6,
          pr: 3,
          borderBottom: isLast ? undefined : "1px solid #e2e8f0",
        }}
      >
        {attempt.errorMessage && (
          <Box sx={{ mb: logsText ? 1.5 : 0 }}>
            <Chip
              label="Error"
              size="small"
              sx={{
                mb: 0.75,
                height: 22,
                fontWeight: 700,
                bgcolor: "#fff1f2",
                color: "#be123c",
              }}
            />
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#1e293b",
                color: "#fda4af",
                fontFamily: "'Roboto Mono', monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 180,
                overflow: "auto",
              }}
            >
              {attempt.errorMessage}
            </Box>
          </Box>
        )}
        {logsText && (
          <Box>
            <Chip
              label="Logs"
              size="small"
              sx={{
                mb: 0.75,
                height: 22,
                fontWeight: 700,
                bgcolor: "#eff6ff",
                color: "#1d4ed8",
              }}
            />
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: theme.palette.mode === "dark" ? "#0f172a" : "#1e293b",
                color: "#93c5fd",
                fontFamily: "'Roboto Mono', monospace",
                fontSize: "0.75rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 240,
                overflow: "auto",
              }}
            >
              {logsText}
            </Box>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

const PlaywrightRetrySubRows = ({
  attempts,
  theme,
  retryOpen,
  showRetryColumn,
  columnCount,
  onOpenImage,
  onOpenVideo,
}) => {
  const [expandedAttemptKeys, setExpandedAttemptKeys] = useState(() => new Set());

  useEffect(() => {
    if (!retryOpen) {
      setExpandedAttemptKeys(new Set());
    }
  }, [retryOpen]);

  if (!retryOpen || !attempts?.length) return null;

  return attempts.map((attempt, idx) => {
    const attemptKey = getRetryAttemptKey(attempt, idx);
    const hasDetail = Boolean(
      attempt.errorMessage || formatLogsForDisplay(attempt.logs)
    );
    const detailOpen = expandedAttemptKeys.has(attemptKey);
    const isLast = idx === attempts.length - 1;

    const handleSubRowClick = () => {
      if (!hasDetail) return;
      setExpandedAttemptKeys((prev) => {
        const next = new Set(prev);
        if (next.has(attemptKey)) {
          next.delete(attemptKey);
        } else {
          next.add(attemptKey);
        }
        return next;
      });
    };

    return (
      <Fragment key={`retry-${attemptKey}-${idx}`}>
        <TableRow
          hover={hasDetail}
          onClick={handleSubRowClick}
          sx={{
            cursor: hasDetail ? "pointer" : "default",
            bgcolor:
              detailOpen
                ? theme.palette.mode === "dark"
                  ? "rgba(99, 102, 241, 0.12)"
                  : "rgba(224, 231, 255, 0.95)"
                : theme.palette.mode === "dark"
                  ? "rgba(99, 102, 241, 0.06)"
                  : "rgba(238, 242, 255, 0.65)",
            "& td": {
              borderBottom:
                isLast && !(detailOpen && hasDetail) ? undefined : "1px solid #e2e8f0",
            },
          }}
        >
          <TableCell sx={{ pl: 3, py: 1.25 }}>
            <SubdirectoryArrowRightIcon sx={{ fontSize: 18, color: "#94a3b8", verticalAlign: "middle" }} />
          </TableCell>
          <TableCell sx={{ py: 1.25 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: "#4338ca" }}>
              {attempt.label || `Attempt ${attempt.attemptNumber}`}
            </Typography>
          </TableCell>
          <TableCell sx={{ py: 1.25 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.8rem",
                color: statusColor(attempt.status, theme),
              }}
            >
              {attempt.status}
            </Typography>
          </TableCell>
          <TableCell align="center" sx={{ py: 1.25 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "#64748b" }}>
              {formatTime(attempt.duration)}
            </Typography>
          </TableCell>
          {showRetryColumn && <TableCell sx={{ py: 1.25 }} />}
          <TableCell align="center" sx={{ py: 1.25 }} onClick={(e) => e.stopPropagation()}>
            <Stack direction="row" spacing={0.5} justifyContent="center">
              <Tooltip
                title={
                  attempt.screenshot ? "View attempt screenshot" : "No screenshot for this attempt"
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!attempt.screenshot}
                    onClick={(e) => onOpenImage(e, attempt.screenshot)}
                    sx={{ color: "#059669" }}
                  >
                    <ImageIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={attempt.video ? "View attempt video" : "No video for this attempt"}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!attempt.video}
                    onClick={(e) => onOpenVideo(e, attempt.video)}
                    sx={{ color: "#7c3aed" }}
                  >
                    <VideocamIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>

        {detailOpen && hasDetail && (
          <AttemptDetailPanel
            attempt={attempt}
            theme={theme}
            isLast={isLast}
            columnCount={columnCount}
          />
        )}
      </Fragment>
    );
  });
};

export default PlaywrightRetrySubRows;
